import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Stack,
  Tabs,
  Tab,
  Divider,
  Alert,
} from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Add as AddIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Receipt as InvoiceIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Send as SendIcon,
  CheckCircle as PaidIcon,
  Schedule as PendingIcon,
  Warning as OverdueIcon,
  Cancel as CancelledIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useLoading } from '../../contexts/LoadingContext';
import { useNotify } from '../ui/NotificationSystem';
import FinancialService, {
  FinancialAnalytics,
  FinancialStats,
  Invoice,
  InvoicePayload,
  InvoiceStatus,
  Payment,
  Pagination as PaginationInfo,
} from '../../services/FinancialService';
import { clientService, Client } from '../../services/ClientService';
import { useProjectService, Project } from '../../services/ProjectService';

type StatusFilter = InvoiceStatus | 'all';

interface ClientOption {
  id: number;
  name: string;
}

interface ProjectOption {
  id: number;
  name: string;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const formatCurrency = (value: number): string => currencyFormatter.format(value);

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString();
};

const formatStatusLabel = (status: string): string =>
  status
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getStatusChipColor = (status: InvoiceStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'sent':
      return 'info';
    case 'overdue':
      return 'error';
    case 'cancelled':
      return 'warning';
    case 'draft':
    default:
      return 'default';
  }
};

const getStatusIcon = (status: InvoiceStatus) => {
  switch (status) {
    case 'paid':
      return <PaidIcon fontSize="small" />;
    case 'sent':
      return <SendIcon fontSize="small" />;
    case 'overdue':
      return <OverdueIcon fontSize="small" />;
    case 'cancelled':
      return <CancelledIcon fontSize="small" />;
    case 'draft':
    default:
      return <InvoiceIcon fontSize="small" />;
  }
};

const FinancialManagement: React.FC = () => {
  const { setLoading } = useLoading();
  const { success: notifySuccess, error: notifyError } = useNotify();
  const { getProjects } = useProjectService();

  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<FinancialStats | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicePagination, setInvoicePagination] = useState<PaginationInfo | null>(null);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<StatusFilter>('all');
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePerPage] = useState(10);
  const [invoiceSearchInput, setInvoiceSearchInput] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsPagination, setPaymentsPagination] = useState<PaginationInfo | null>(null);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [analytics, setAnalytics] = useState<FinancialAnalytics | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [projects, setProjectsState] = useState<ProjectOption[]>([]);

  // Debounce search input for invoices
  useEffect(() => {
    const handler = setTimeout(() => {
      setInvoiceSearch(invoiceSearchInput.trim());
      setInvoicePage(1);
    }, 400);

    return () => clearTimeout(handler);
  }, [invoiceSearchInput]);

  const loadStats = useCallback(async () => {
    try {
      const data = await FinancialService.getStats();
      setStats(data);
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Unable to load financial statistics');
    }
  }, [notifyError]);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading('financial.invoices', true);
      const response = await FinancialService.getInvoices({
        status: invoiceStatusFilter,
        search: invoiceSearch,
        page: invoicePage,
        perPage: invoicePerPage,
      });

      setInvoices(response.invoices);
      setInvoicePagination(response.pagination);
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Unable to load invoices');
    } finally {
      setLoading('financial.invoices', false);
    }
  }, [invoiceStatusFilter, invoiceSearch, invoicePage, invoicePerPage, notifyError, setLoading]);

  const loadPayments = useCallback(async () => {
    try {
      setLoading('financial.payments', true);
      const response = await FinancialService.getPayments({ page: paymentsPage, perPage: 10 });

      setPayments(response.payments);
      setPaymentsPagination(response.pagination);
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Unable to load payments');
    } finally {
      setLoading('financial.payments', false);
    }
  }, [paymentsPage, notifyError, setLoading]);

  const loadAnalytics = useCallback(async () => {
    try {
      const data = await FinancialService.getAnalytics();
      setAnalytics(data);
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Unable to load analytics');
    }
  }, [notifyError]);

  const loadClients = useCallback(async () => {
    try {
      const response = await clientService.getClients({ per_page: 100 });
      const mapped = response.clients.map((client: Client) => ({
        id: client.id,
        name: client.company ?? client.name,
      }));
      setClients(mapped);
    } catch (error) {
      console.error('Failed to load clients', error);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const projectList = await getProjects({ per_page: 100 });
      const mapped = projectList.map((project: Project) => ({
        id: project.id,
        name: project.name,
      }));
      setProjectsState(mapped);
    } catch (error) {
      console.error('Failed to load projects', error);
    }
  }, [getProjects]);

  useEffect(() => {
    loadStats();
    loadAnalytics();
    loadClients();
    loadProjects();
  }, [loadStats, loadAnalytics, loadClients, loadProjects]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    const value = event.target.value as StatusFilter;
    setInvoiceStatusFilter(value);
    setInvoicePage(1);
  };

  const handleInvoicePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setInvoicePage(value);
  };

  const handlePaymentsPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPaymentsPage(value);
  };

  const handleCreateInvoice = () => {
    setEditingInvoice(null);
    setInvoiceDialogOpen(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setInvoiceDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveInvoice = async (payload: InvoicePayload) => {
    setIsSavingInvoice(true);
    try {
      if (editingInvoice) {
        await FinancialService.updateInvoice(editingInvoice.id, payload);
        notifySuccess('Invoice updated successfully');
      } else {
        await FinancialService.createInvoice(payload);
        notifySuccess('Invoice created successfully');
      }

      setInvoiceDialogOpen(false);
      setEditingInvoice(null);
      await Promise.all([loadInvoices(), loadStats(), loadAnalytics()]);
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Unable to save invoice');
    } finally {
      setIsSavingInvoice(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const revenueInsights = useMemo(() => {
    if (!analytics) {
      return { bestMonth: null as string | null };
    }

    if (analytics.revenueByMonth.length === 0) {
      return { bestMonth: null };
    }

    const best = analytics.revenueByMonth.reduce((prev, current) =>
      current.revenue > prev.revenue ? current : prev
    );

    return { bestMonth: best.month };
  }, [analytics]);

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Financial Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track invoices, payments, and revenue trends across the organization.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateInvoice}>
          New Invoice
        </Button>
      </Box>

      {stats && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 3,
            mb: 3,
          }}
        >
          <FinancialStatCard
            title="Total Revenue"
            icon={<TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />}
            value={formatCurrency(stats.totalRevenue)}
            caption="All-time revenue from paid invoices"
          />
          <FinancialStatCard
            title="This Month"
            icon={<MoneyIcon color="success" sx={{ fontSize: 40 }} />}
            value={formatCurrency(stats.monthlyRevenue)}
            caption="Paid invoices recorded this month"
          />
          <FinancialStatCard
            title="Pending Invoices"
            icon={<PendingIcon color="warning" sx={{ fontSize: 40 }} />}
            value={stats.pendingInvoices.toString()}
            caption="Invoices awaiting payment"
          />
          <FinancialStatCard
            title="Outstanding"
            icon={<InvoiceIcon color="error" sx={{ fontSize: 40 }} />}
            value={formatCurrency(stats.outstandingAmount)}
            caption="Balance remaining on open invoices"
          />
        </Box>
      )}

      <Card>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
          <Typography variant="h6">Revenue Operations</Typography>
          {revenueInsights.bestMonth && (
            <Chip
              color="primary"
              label={`Top Month: ${revenueInsights.bestMonth}`}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </CardContent>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ px: 2 }}
        >
          <Tab label="Invoices" />
          <Tab label="Payments" />
          <Tab label="Analytics" />
        </Tabs>
        <Divider />
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              mb={3}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flex={1}>
                <TextField
                  fullWidth
                  label="Search invoices"
                  placeholder="Search by invoice number, client, or project"
                  value={invoiceSearchInput}
                  onChange={(event) => setInvoiceSearchInput(event.target.value)}
                />
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="invoice-status-label">Status</InputLabel>
                  <Select
                    labelId="invoice-status-label"
                    label="Status"
                    value={invoiceStatusFilter}
                    onChange={handleStatusFilterChange}
                    size="small"
                  >
                    <MenuItem value="all">All statuses</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="sent">Sent</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            {invoices.length === 0 ? (
              <Alert severity="info">No invoices found for the selected filters.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Issue Date</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {invoice.invoiceNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>{invoice.clientName ?? '—'}</TableCell>
                        <TableCell>{invoice.projectName ?? '—'}</TableCell>
                        <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(invoice.balanceDue)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(invoice.status)}
                            label={formatStatusLabel(invoice.status)}
                            color={getStatusChipColor(invoice.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(invoice.issueDate)}</TableCell>
                        <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                        <TableCell align="center">
                          <IconButton onClick={(event) => handleMenuOpen(event, invoice)} size="small">
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {invoicePagination && invoicePagination.last_page > 1 && (
              <Box mt={3} display="flex" justifyContent="flex-end">
                <MuiPagination
                  color="primary"
                  count={invoicePagination.last_page}
                  page={invoicePagination.current_page}
                  onChange={handleInvoicePageChange}
                />
              </Box>
            )}
          </Box>
        )}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {payments.length === 0 ? (
              <Alert severity="info">No payment records found.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Payment Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>{payment.invoiceNumber ?? `#${payment.invoiceId}`}</TableCell>
                        <TableCell>{payment.clientName ?? '—'}</TableCell>
                        <TableCell align="right">{formatCurrency(payment.amount)}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatusLabel(payment.status)}
                            size="small"
                            color={
                              payment.status === 'completed'
                                ? 'success'
                                : payment.status === 'pending'
                                ? 'warning'
                                : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell>{formatStatusLabel(payment.paymentMethod)}</TableCell>
                        <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {paymentsPagination && paymentsPagination.last_page > 1 && (
              <Box mt={3} display="flex" justifyContent="flex-end">
                <MuiPagination
                  color="primary"
                  count={paymentsPagination.last_page}
                  page={paymentsPagination.current_page}
                  onChange={handlePaymentsPageChange}
                />
              </Box>
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            {analytics ? (
              <Stack spacing={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Revenue by Month
                    </Typography>
                    {analytics.revenueByMonth.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No revenue recorded yet.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Month</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analytics.revenueByMonth.map((entry) => (
                            <TableRow key={entry.month}>
                              <TableCell>{entry.month}</TableCell>
                              <TableCell align="right">{formatCurrency(entry.revenue)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Clients
                    </Typography>
                    {analytics.topClients.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No client revenue recorded yet.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Client</TableCell>
                            <TableCell align="right">Total Revenue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analytics.topClients.map((client) => (
                            <TableRow key={client.name}>
                              <TableCell>{client.name}</TableCell>
                              <TableCell align="right">{formatCurrency(client.revenue)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Invoice Status Distribution
                    </Typography>
                    {analytics.invoiceStatusDistribution.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No invoices available.
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        {analytics.invoiceStatusDistribution.map((entry) => (
                          <Chip
                            key={entry.status}
                            label={`${formatStatusLabel(entry.status)} • ${entry.count} (${entry.percentage}%)`}
                            color={getStatusChipColor(entry.status)}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            ) : (
              <Alert severity="info">Analytics data is not available yet.</Alert>
            )}
          </Box>
        )}
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedInvoice && handleEditInvoice(selectedInvoice)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Invoice
        </MenuItem>
        <MenuItem disabled>
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Download PDF
        </MenuItem>
      </Menu>
      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => {
          setInvoiceDialogOpen(false);
          setEditingInvoice(null);
        }}
        onSave={handleSaveInvoice}
        invoice={editingInvoice ?? undefined}
        clients={clients}
        projects={projects}
        isSaving={isSavingInvoice}
      />
    </Box>
  );
};

interface FinancialStatCardProps {
  title: string;
  value: string;
  caption: string;
  icon: React.ReactNode;
}

const FinancialStatCard: React.FC<FinancialStatCardProps> = ({ title, value, caption, icon }) => (
  <Card>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        </Box>
        {icon}
      </Box>
    </CardContent>
  </Card>
);

interface InvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (payload: InvoicePayload) => void;
  invoice?: Invoice;
  clients: ClientOption[];
  projects: ProjectOption[];
  isSaving: boolean;
}

interface InvoiceFormState {
  invoiceNumber: string;
  clientId: string;
  projectId: string;
  amount: string;
  status: InvoiceStatus;
  issueDate: string;
  dueDate: string;
  paidDate: string;
  description: string;
  notes: string;
}

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({ open, onClose, onSave, invoice, clients, projects, isSaving }) => {
  const [form, setForm] = useState<InvoiceFormState>({
    invoiceNumber: '',
    clientId: '',
    projectId: '',
    amount: '0',
    status: 'draft',
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString().slice(0, 10),
    paidDate: '',
    description: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (invoice) {
      setForm({
        invoiceNumber: invoice.invoiceNumber ?? '',
        clientId: invoice.clientId !== undefined && invoice.clientId !== null ? invoice.clientId.toString() : '',
        projectId: invoice.projectId !== undefined && invoice.projectId !== null ? invoice.projectId.toString() : '',
        amount: invoice.amount.toString(),
        status: invoice.status,
        issueDate: invoice.issueDate ?? new Date().toISOString().slice(0, 10),
        dueDate: invoice.dueDate ?? new Date().toISOString().slice(0, 10),
        paidDate: invoice.paidDate ?? '',
        description: invoice.description ?? '',
        notes: invoice.notes ?? '',
      });
    } else {
      setForm({
        invoiceNumber: '',
        clientId: clients[0]?.id ? clients[0]?.id.toString() : '',
        projectId: '',
        amount: '0',
        status: 'draft',
        issueDate: new Date().toISOString().slice(0, 10),
        dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString().slice(0, 10),
        paidDate: '',
        description: '',
        notes: '',
      });
    }
    setFormErrors({});
  }, [invoice, clients, open]);

  const handleChange = (field: keyof InvoiceFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (field: keyof InvoiceFormState) => (event: SelectChangeEvent) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value as string,
    }));
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.clientId) {
      errors.clientId = 'Client is required';
    }

    const amountValue = Number(form.amount);
    if (Number.isNaN(amountValue) || amountValue <= 0) {
      errors.amount = 'Amount must be greater than zero';
    }

    if (form.dueDate && form.issueDate && form.dueDate < form.issueDate) {
      errors.dueDate = 'Due date must be on or after the issue date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload: InvoicePayload = {
      invoice_number: form.invoiceNumber || undefined,
      client_id: Number(form.clientId),
      project_id: form.projectId === '' ? null : Number(form.projectId),
      amount: Number(form.amount),
      status: form.status,
      issue_date: form.issueDate || null,
      due_date: form.dueDate || null,
      paid_date: form.status === 'paid' ? form.paidDate || null : null,
      description: form.description || undefined,
      notes: form.notes || undefined,
    };

    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{invoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Invoice Number"
              value={form.invoiceNumber}
              onChange={handleChange('invoiceNumber')}
              placeholder="Leave blank to auto-generate"
              fullWidth
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth error={Boolean(formErrors.clientId)}>
                <InputLabel id="invoice-client-label">Client</InputLabel>
                <Select
                  labelId="invoice-client-label"
                  label="Client"
                  value={form.clientId}
                  onChange={handleSelectChange('clientId')}
                  required
                  displayEmpty
                >
                  {clients.length === 0 && <MenuItem value="">No clients available</MenuItem>}
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id.toString()}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.clientId && (
                  <Typography variant="caption" color="error">
                    {formErrors.clientId}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="invoice-project-label">Project</InputLabel>
                <Select
                  labelId="invoice-project-label"
                  label="Project"
                  value={form.projectId}
                  onChange={handleSelectChange('projectId')}
                  displayEmpty
                >
                  <MenuItem value="">No project</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={form.amount}
                onChange={handleChange('amount')}
                required
                error={Boolean(formErrors.amount)}
                helperText={formErrors.amount}
              />

              <FormControl fullWidth>
                <InputLabel id="invoice-status-select">Status</InputLabel>
                <Select
                  labelId="invoice-status-select"
                  label="Status"
                  value={form.status}
                  onChange={handleSelectChange('status')}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Issue Date"
                type="date"
                value={form.issueDate}
                onChange={handleChange('issueDate')}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={form.dueDate}
                onChange={handleChange('dueDate')}
                InputLabelProps={{ shrink: true }}
                error={Boolean(formErrors.dueDate)}
                helperText={formErrors.dueDate}
              />
              <TextField
                fullWidth
                label="Paid Date"
                type="date"
                value={form.paidDate}
                onChange={handleChange('paidDate')}
                InputLabelProps={{ shrink: true }}
                disabled={form.status !== 'paid'}
              />
            </Stack>

            {invoice && (
              <TextField
                fullWidth
                label="Current Balance"
                value={formatCurrency(invoice.balanceDue)}
                InputProps={{ readOnly: true }}
              />
            )}

            <TextField
              fullWidth
              label="Description"
              multiline
              minRows={3}
              value={form.description}
              onChange={handleChange('description')}
            />

            <TextField
              fullWidth
              label="Internal Notes"
              multiline
              minRows={2}
              value={form.notes}
              onChange={handleChange('notes')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSaving}>
            {invoice ? 'Update Invoice' : 'Create Invoice'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FinancialManagement;
